import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator,
} from '@mui/icons-material';
import { useCollection } from '../hooks/useFirestore';
import {
  query as fbQuery,
  orderBy as fbOrderBy,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

type CategoryStatus = 'visible' | 'hidden';

interface FAQCategory {
  id: string;
  name: string;
  order: number;
  status: CategoryStatus;
}

const INITIAL_CATEGORIES: { name: string }[] = [
  { name: 'アカウント登録について' },
  { name: 'クレジットカード決済について' },
  { name: '月謝袋について' },
  { name: 'コース登録について' },
  { name: 'アカウントの削除について' },
  // 提案
  { name: '料金・請求・領収書' },
  { name: 'ログイン・パスワード・セキュリティ' },
  { name: '不具合/トラブルシューティング' },
  { name: 'その他' },
];

export default function FAQCategoriesPage() {
  const { data: categories } = useCollection<FAQCategory>(
    'faqCategories',
    (q) => fbQuery(q, fbOrderBy('order', 'asc'))
  );

  const sorted = useMemo(() => {
    const arr = categories || [];
    return [...arr].sort((a: any, b: any) => {
      const ao = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
      const bo = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });
  }, [categories]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FAQCategory | null>(null);
  const [name, setName] = useState('');
  const [status, setStatus] = useState<CategoryStatus>('visible');

  const openCreate = () => {
    setEditing(null);
    setName('');
    setStatus('visible');
    setDialogOpen(true);
  };

  const openEdit = (cat: FAQCategory) => {
    setEditing(cat);
    setName(cat.name || '');
    setStatus((cat.status as CategoryStatus) || 'visible');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      alert('カテゴリ名を入力してください');
      return;
    }
    try {
      if (editing) {
        await updateDoc(doc(db, 'faqCategories', editing.id), {
          name: trimmed,
          status,
        });
      } else {
        const nextOrder = (sorted?.length || 0) + 1;
        await addDoc(collection(db, 'faqCategories'), {
          name: trimmed,
          order: nextOrder,
          status,
          createdAt: serverTimestamp(),
        });
      }
      setDialogOpen(false);
    } catch (e) {
      console.error('Failed to save FAQ category:', e);
      alert('保存に失敗しました');
    }
  };

  const handleDelete = async (cat: FAQCategory) => {
    if (!window.confirm(`カテゴリ「${cat.name}」を削除しますか？\n※このカテゴリを参照しているFAQは「未分類」として扱われます。`)) return;
    try {
      await deleteDoc(doc(db, 'faqCategories', cat.id));
    } catch (e) {
      console.error('Failed to delete FAQ category:', e);
      alert('削除に失敗しました');
    }
  };

  const handleToggleStatus = async (cat: FAQCategory, next: boolean) => {
    try {
      await updateDoc(doc(db, 'faqCategories', cat.id), {
        status: next ? 'visible' : 'hidden',
      });
    } catch (e) {
      console.error('Failed to update category status:', e);
    }
  };

  const handleCreateInitial = async () => {
    if (!window.confirm('初期カテゴリを作成しますか？')) return;
    try {
      const batch = INITIAL_CATEGORIES.map((c, i) =>
        addDoc(collection(db, 'faqCategories'), {
          name: c.name,
          order: i + 1,
          status: 'visible',
          createdAt: serverTimestamp(),
        })
      );
      await Promise.all(batch);
    } catch (e) {
      console.error('Failed to create initial categories:', e);
      alert('作成に失敗しました');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">FAQカテゴリ管理</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(sorted?.length || 0) === 0 && (
            <Button variant="outlined" onClick={handleCreateInitial}>
              初期カテゴリを作成
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            追加
          </Button>
        </Box>
      </Box>

      <Card>
        <List>
          {sorted?.map((cat, idx) => (
            <ListItem
              key={cat.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', String(idx));
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={async (e) => {
                e.preventDefault();
                const fromIndex = Number(e.dataTransfer.getData('text/plain'));
                const toIndex = idx;
                if (Number.isNaN(fromIndex) || fromIndex === toIndex) return;

                const reordered = [...sorted];
                const [moved] = reordered.splice(fromIndex, 1);
                reordered.splice(toIndex, 0, moved);

                try {
                  const updates = reordered.map((item, i) =>
                    updateDoc(doc(db, 'faqCategories', item.id), { order: i + 1 })
                  );
                  await Promise.all(updates);
                } catch (err) {
                  console.error('Failed to reorder FAQ categories:', err);
                }
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    edge="start"
                    sx={{ cursor: 'grab', '&:hover': { cursor: 'grab' }, '&:active': { cursor: 'grabbing' } }}
                  >
                    <DragIndicator />
                  </IconButton>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        checked={(cat as any).status !== 'hidden'}
                        onChange={(e) => handleToggleStatus(cat, e.target.checked)}
                      />
                    }
                    label={(cat as any).status === 'hidden' ? '非表示' : '表示'}
                  />
                  <IconButton edge="end" onClick={() => openEdit(cat)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleDelete(cat)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={cat.name}
                secondary={`表示順: ${typeof (cat as any).order === 'number' ? (cat as any).order : '—'}`}
              />
            </ListItem>
          ))}
        </List>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'カテゴリ編集' : 'カテゴリ追加'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="カテゴリ名"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={status === 'visible'}
                onChange={(e) => setStatus(e.target.checked ? 'visible' : 'hidden')}
              />
            }
            label={status === 'visible' ? '表示' : '非表示'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSave}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

